import React from 'react';
import { useQuery } from 'react-apollo';
import { BasePage, Helmet, FourOhFourErrorPage, SurveyTheme } from '@sm/webassets';
import { t } from '@sm/intl';
import { RespondentCapabilityProvider } from '@sm/question-widgets/respondent-survey';
import Logo from '~app/components/Survey/Logo';
import { BypassBlock } from '~app/components/core/BypassBlock';

import { Layout } from './components/Layout';
import { ExitLink } from '~app/components/Survey/ExitLink';
import { SurveyTitle } from '~app/components/Survey/SurveyTitleV2';
import SurveyFooter from '~app/components/Survey/FooterContainer';
import { PageTitle } from '~app/components/Survey/PageTitle';
import { PageDescription } from '~app/components/Survey/PageDescription';
import { SurveyButton } from '~app/components/Survey/SurveyButton';
import { PersistentProgressBar } from '~app/components/Survey/PersistentProgressBar';

import { QuestionListController } from './components/QuestionListController';
import { getSurveyIdFromCollector, getSurveyDetails } from './SurveyTaking.graphql';
import createHeadingLevel from './helper/createHeadingLevel';
import extrapolateSurveyData from './helper/extrapolateSurveyData';
import useStyles from './useStyles';

const COPY = {
  META_DESCRIPTION: {
    id: 'SurveyTaking.metaDescription',
    defaultMessage:
      "Web survey powered by SurveyMonkey.com. Create your own online survey now with SurveyMonkey's expert certified FREE templates.",
  },
};

const SurveyTaking = () => {
  //
  /**
   * TODO: get url from StaticContext, convert slug to collectorId.
   *
   * '55804393' (Tarun) for @see https://www.monkeytest3.com/rx/77JC2ZQ
   * '510967475' (Thomas) for @see https://www.monkeytest1.com/rx/P8VKGS9
   * '28753656' (Aldrin) for @see https://www.monkeytest1.com/rx/YMMCSS2
   * '510854224' (Douglas) for @see https://www.monkeytest1.com/r/K58WSQ3
   */
  const collectorId = '55804393';

  const {
    loading: loadingCollector,
    error: errorCollector,
    data: collectorData,
  } = useQuery(getSurveyIdFromCollector, {
    variables: {
      collectorId,
    },
  });

  if (errorCollector) {
    throw errorCollector;
  }

  const {
    loading: loadingSurvey,
    error: errorSurvey,
    data: surveyData,
  } = useQuery(getSurveyDetails, {
    skip: !collectorData || loadingCollector,
    variables: {
      surveyId: collectorData?.collector?.surveyId,
    },
  });

  const {
    surveyTitleContainer,
    pageHeaderContainer,
    questionContainer,
    pageFooterContainer,
    pageNavigationContainer,
    navButtons,
  } = useStyles();

  if (errorSurvey) {
    throw errorSurvey;
  }

  if (!loadingSurvey && surveyData.survey === null) {
    return <FourOhFourErrorPage />;
  }

  if (surveyData) {
    const surveyId = collectorData?.collector?.surveyId;
    const { surveyTitle, surveyTitleHTML, questions, langCode, isAutoScroll, totalQuestionsCount, surveyPages } =
      extrapolateSurveyData(surveyData?.survey);
    const isRTL = ['ar', 'he', 'fa'].indexOf(langCode) > -1;

    /**
     * Design Vars
     * design and layout related variables
     */
    const {
      footer: { enabled: isFooterEnabled, grayscale },
      surveyTitle: { enabled: isSurveyTitleEnabled, horizontalAlignment: surveyTitleAlignment },
      showPageTitles,
      showPageNumbers,
      logo: logoProps,
      questionNumbering: { enabled: isQuestionNumberingEnabled },
      showRequiredQuestionAsterisks,
      // MISSING: isExitLinkEnabled - N/A in Query
      // MISSING: URL (HREF) for exit-link
      isExitLinkEnabled = true,
      progressBar: { enabled: isProgressBarEnabled },
    } = surveyData?.survey?.design;

    /**
     * Pages Vars
     * The data below may be consumed and calculated via
     * a `SurveyProvider` or `PageProvider`
     */
    const currentPage = 0;

    /**
     * Page Header
     * calculate proper Page Title and Description based on current page
     */
    const { heading: pageHeading, subHeading: pageDescription } = surveyPages[currentPage];
    const pageTitle = showPageNumbers ? `${currentPage + 1}. ${pageHeading}` : pageHeading;
    const hasPageDescription = !!pageDescription;

    /**
     * Skip to content will attempt to move the focus position (without actually focusing)
     * to the Survey Title (if exists), then Page title (if exists), finially to the
     * main-content container (<main>).
     * `tabIndex={-1}` is added to each element (backwards compat with <= IE 11)
     */
    const surveyTitleId = `survey-title-${surveyId}`;
    const pageTitleId = `page-title-${surveyId}`;
    const mainContentId = `main-content-${surveyId}`;
    let skipContentId = mainContentId;
    if (isSurveyTitleEnabled) {
      skipContentId = surveyTitleId;
    } else if (showPageTitles) {
      skipContentId = pageTitleId;
    }

    const nextHeadingElement = createHeadingLevel();
    const settings = {
      survey: {
        numberingEnabled: isQuestionNumberingEnabled,
        asteriskEnabled: showRequiredQuestionAsterisks,
      },
    };

    // TODO: use react-jss for theme in Header/Footer components when replacing
    return (
      <BasePage
        actionFlow="SurveyTaking"
        pageId="SurveyTaking"
        legacyWeb="respweb"
        includeHeader={false}
        includeFooter={false}
      >
        <Helmet htmlAttributes={{ lang: langCode }}>
          <title>{surveyTitle} Survey</title>
          <meta name="description" content={t(COPY.META_DESCRIPTION)} />
          <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=yes" />
          <meta httpEquiv="content-language" content={langCode} />
        </Helmet>
        <SurveyTheme surveyId={surveyId} targetEndPoint="surveyTaking">
          <BypassBlock>
            <a href={`#${skipContentId}`}>Go to content</a>
          </BypassBlock>
          {/* <SurveyProvider> */}
          <Layout
            ids={{ mainContent: mainContentId }}
            isRTL={isRTL}
            logo={logoProps.enabled && <Logo logo={logoProps} />}
            tools={isExitLinkEnabled && <ExitLink href="#">Exit</ExitLink>}
          >
            {/* <PageProvider> */}
            {/* <ProgressionProvider> */}

            <>
              <div id={`survey-title-container-${surveyId}`} className={surveyTitleContainer}>
                <If condition={isSurveyTitleEnabled}>
                  <SurveyTitle
                    id={surveyTitleId}
                    align={surveyTitleAlignment}
                    element={nextHeadingElement(0)}
                    text={surveyTitleHTML}
                    tabIndex={-1}
                  />
                </If>
                {/* <SurveyIntro text={surveyIntro} /> */}
                {/* <OKButton aria-label="Continue" /> */}
              </div>

              <header id={`page-header-container-${surveyId}`} className={pageHeaderContainer}>
                <If condition={showPageTitles}>
                  <PageTitle id={pageTitleId} element={nextHeadingElement(1)} text={pageTitle} tabIndex={-1} />
                </If>
                <If condition={hasPageDescription}>
                  <PageDescription text={pageDescription} />
                </If>
                <SurveyButton size="md">OK</SurveyButton>
              </header>

              <RespondentCapabilityProvider settings={settings}>
                <form id={`form-${surveyId}`} className={questionContainer}>
                  {/**
                   * NOTE: active (boolean) will be controlled by Progression Engine
                   * NOTE: Are all the nullified fields (questionBank, validation) needed?
                   */}

                  <QuestionListController
                    questions={questions.map(q => ({
                      ...q,
                      active: true,
                      questionBank: null,
                      validation: null,
                      errors: [],
                    }))}
                  />
                  {/**
                   *  When survey is in OQAAT mode, progress bar is below the survey footer and is sticky
                   */}
                  <If condition={isProgressBarEnabled && isAutoScroll}>
                    <PersistentProgressBar value={1} total={totalQuestionsCount} />
                  </If>
                  <div className={pageNavigationContainer}>
                    <SurveyButton className={navButtons} size="lg">
                      GO BACK A PAGE
                    </SurveyButton>
                    <SurveyButton className={navButtons} size="lg">
                      DONE
                    </SurveyButton>
                    <SurveyButton className={navButtons} size="lg">
                      GO FORWARD A PAGE
                    </SurveyButton>
                  </div>
                </form>
              </RespondentCapabilityProvider>

              <footer id={`page-footer-container-${surveyId}`} className={pageFooterContainer}>
                {/* <PageNavigation /> */}
                PAGE FOOTER
              </footer>
            </>
            <If condition={isFooterEnabled}>
              <SurveyFooter
                id={`survey-footer-container-${surveyId}`}
                isGrayScale={grayscale}
                showPrivacyCookiePolicy
              />
            </If>
            {/* </ProgressionProvider> */}
            {/* </PageProvider> */}
          </Layout>
          {/* </SurveyProvider> */}
        </SurveyTheme>
      </BasePage>
    );
  }
  return null;
};

export default SurveyTaking;
